import { useEffect, useRef, useState, useCallback } from 'react';
import "./GenericSelect.css";

export type GenericSelectOption = {
    displayName: string;
    value: any;
    image?: string;
    component?: JSX.Element;
}

type GenericSelectProps = {
    selected?: GenericSelectOption;
    disabled?: boolean;
    options: GenericSelectOption[];
    onChange: (value: any) => void;
    maxHeight?: string;
    circleImage?: boolean;
}

export const GenericSelect = ({ selected, disabled = false, options, onChange, maxHeight, circleImage = false, ...rest }: GenericSelectProps) => {

	const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const isOpeningRef = useRef(false);

    const eventKeyDown = useCallback((e: any) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setIsOpen(false);
        }
    }, []);

    const eventMouseDown = useCallback((e: any) => {
        // Ignore if we just opened the dropdown (prevent immediate close)
        if (isOpeningRef.current) {
            isOpeningRef.current = false;
            return;
        }

        const current = selectRef.current as any;

        // Only close if clicking outside and dropdown is actually open
        if (isOpen && current && !current.contains(e.target)) {
            setIsOpen(false);
        }
    }, [isOpen]);

    const handleClick = (e: any) => {
        e.stopPropagation(); // Prevent event from bubbling up
        
        if (!isOpen) {
            // Mark that we're opening to prevent immediate close
            isOpeningRef.current = true;
            // Reset the flag after a short delay
            setTimeout(() => {
                isOpeningRef.current = false;
            }, 100);
        }
        
        setIsOpen(!isOpen);
    }

    useEffect(() => {
        if (isOpen) {
            // Delay attaching listener slightly to avoid catching the opening click
            const timeoutId = setTimeout(() => {
                document.addEventListener('keydown', eventKeyDown, true);
                document.addEventListener('mousedown', eventMouseDown);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('keydown', eventKeyDown, true);
                document.removeEventListener('mousedown', eventMouseDown);
            };
        }

        return () => {
            document.removeEventListener('keydown', eventKeyDown, true);
            document.removeEventListener('mousedown', eventMouseDown);
        };
    }, [isOpen, eventKeyDown, eventMouseDown]);

    
    // console.log("[GenericSelect] selected", selected);

    return (
		<div className="generic-select-container">
            <div 
                className="generic-select"
                onClick={disabled ? undefined : handleClick}
                style={{
                    ...(isOpen ? {zIndex: 101} :{}),
                    ...(maxHeight ? {maxHeight: maxHeight} : {})
                    }}
                ref={selectRef}
            >
                <div 
                    className="generic-select-selected"
                >
					{selected && selected.image && 
                        <img src={selected.image} style={{ borderRadius: circleImage ? '50%' : '0%' }} />
                    }
                    {selected && selected.component &&
                        selected.component
                    }
					{selected && selected.displayName}
                </div>

                {isOpen &&
                    <div className="generic-select-options">
                        {options.map((option: GenericSelectOption) => {
                            return (				
                                <div 
                                    key={option.value}
                                    className="generic-select-option"
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }}
                                >
                                    {option.component &&
                                        option.component
                                    }
									{option.image && 
                                        <img src={option.image} style={{ borderRadius: circleImage ? '50%' : '0%' }} />
                                    }
									{option.displayName}
                                </div>
                            )
                        }
                        )}
                    </div>
                }

            </div>
        </div>
    )
}
